<template>
    <div style="width:100;height:100%;background:#F4F4F4">
        <van-sticky>
            <NavBar :bgColor="'#1A3232'"/>
            <van-tabs
                @change="handleTabChange"
                v-model="active"
                title-inactive-color="rgba(255, 255, 255, 0.50)"
                title-active-color="#fff"
                color="#fff"
                background="rgb(26, 50, 50)"
                line-height="2px"
            >
                <van-tab v-for="item in tabs" :title="item.label" :key="item.id" :name="item.id"></van-tab>
            </van-tabs>
        </van-sticky>
        <div class="orderData">
            <div v-for="item in orderData" :key="item.id">
                <OrderItem :rows="item" @refreshOrder="refreshOrder"/>
            </div>
            <!-- 分页：所有 tab 底部都展示 -->
            <div v-if="pageCount >= 1" class="paginationBox">
                <van-pagination
                    v-model="queryForm.page"
                    :page-count="pageCount"
                    mode="simple"
                    prev-text="Previous"
                    next-text="Next"
                    @change="handlePageChange"
                />
            </div>
        </div>
        <van-skeleton title :row="10" :loading="skeletonloading"></van-skeleton>
        <van-empty v-if="showEmpty" description="No data" />
    </div>
</template>
<script>
import NavBar from '@/components/NavBar/index.vue'
import OrderItem from '@/components/Order/index.vue'
import { ensureUserLngLat } from '@/utils/location'
export default {
    name:'Order',
    data(){
        return {
            // 默认展示“All”
            active:'all',
            list: [],
            loading:false,
            skeletonloading:false,
            showEmpty:false,
            // 订单状态筛选，与设计图一致
            tabs:[
                { id:'all',       label:'All' },
                { id:'wait_payment',    label:'Pending Payment' },
                { id:'already_payment',      label:'Paid' },
                { id:'completed', label:'Completed' },
                { id:'cancel',    label:'Cancelled' },
                { id:'evaluate', label:'Reviewed' },
            ],
            orderData:[],
            totalItems: 0,
            pageCount: 0,
            // 请求参数
            queryForm:{
                page:1,
                list_rows:10,
                // 与 tabs 的 id 对应
                dataType:'all',
            }
        }
    },
    components:{
        NavBar,
        OrderItem
    },
    created(){
        // 检查路由参数，切换到对应的标签
        const tab = this.$route.query.tab
        if(tab){
            this.active = tab
            this.queryForm.dataType = tab
        }
        // 初始化加载（之前由 van-list 触发；现在改为显式调用）
        this.load()
    },
    methods:{
        scrollToTop(){
            // 翻页/切换 tab 后回到顶部（兼容不同浏览器）
            try {
                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
            } catch (e) {
                // fallback
                document.documentElement.scrollTop = 0
                document.body.scrollTop = 0
            }
        },
        async load(){
            // Best-effort: fetch/cache user's lng/lat before requesting order list.
            await ensureUserLngLat().catch(() => {})
            // 每次换页/换 tab 都按页请求；仅第一页显示骨架屏
            this.skeletonloading = this.queryForm.page == 1
            this.showEmpty = false
            this.loading = true
            try {
                const res = await this.$api.getOrderList(this.queryForm)
                if(res.code == 1){
                    const list = (res.data && res.data.list) ? res.data.list : {}
                    const data = (list && list.data) ? list.data : []
                    // 分页信息：优先使用后端分页字段
                    this.totalItems = Number(list.total || list.total_count || list.totalCount || list.count || 0)
                    this.pageCount =
                        Number(list.last_page || list.lastPage || list.page_count || list.pageCount || 0) ||
                        (this.totalItems ? Math.ceil(this.totalItems / this.queryForm.list_rows) : 0)
                    // 后端不返回分页字段时兜底：至少展示 1/1，并在“可能还有下一页”时给出下一页
                    if (!this.pageCount || this.pageCount < 1) {
                        if (data.length === this.queryForm.list_rows) {
                            this.pageCount = this.queryForm.page + 1
                        } else {
                            this.pageCount = this.queryForm.page || 1
                        }
                    }
                    // 收集所有不重复的 shop_supplier_id
                    const supplierIds = [...new Set(data.map(item => item.shop_supplier_id).filter(id => id))]
                    // 批量获取商家详情
                    const supplierDetailsMap = {}
                    await Promise.all(supplierIds.map(async (supplierId) => {
                        try {
                            const detailRes = await this.$api.getSupplierDetail({ shop_supplier_id: supplierId })
                            if (detailRes.code == 1 && detailRes.data.detail) {
                                supplierDetailsMap[supplierId] = detailRes.data.detail
                            }
                        } catch (e) {
                            console.error('Failed to get merchant details:', e)
                        }
                    }))
                    // 将商家详情合并到订单数据中
                    const enrichedData = data.map(item => ({
                        ...item,
                        supplierDetail: supplierDetailsMap[item.shop_supplier_id] || null
                    }))
                    this.orderData = enrichedData
                    // 不显示骨架屏
                    this.skeletonloading = false
                    if (this.queryForm.page == 1 && this.orderData.length == 0 && this.skeletonloading == false) {
                        this.showEmpty = true
                    }
                    this.loading = false
                } else {
                    // 异常情况：结束加载，避免卡住
                    this.skeletonloading = false
                    this.loading = false
                }
            } catch (error) {
                console.error('Failed to load order list:', error)
                this.skeletonloading = false
                this.loading = false
            }
        },
        refreshOrder(){
            this.queryForm.page = 1
            this.load()
        },
        handleTabChange(name,title){
            this.queryForm.page = 1
            this.queryForm.dataType = name
            // 切换 tab 时先重置分页显示，避免沿用上一个 tab 的页数
            this.pageCount = 1
            this.totalItems = 0
            this.scrollToTop()
            this.load()
        },
        handlePageChange(page){
            // v-model 已经更新 queryForm.page，这里只负责重新拉取当前页
            this.queryForm.page = page
            this.scrollToTop()
            this.load()
        }
    }
}
</script>
<style lang="scss" scoped>
.orderData{
    width: 100%;
    background: #F4F4F4;
    padding: 1rem;
    box-sizing: border-box;
}
.paginationBox{
    margin-top: 0.75rem;
    padding-bottom: 0.75rem;
}
</style>
